import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { APIError } from '@vizit/auth';
import type { BenchmarkPickerEntry, BenchmarkPickerView, Retailer } from '../../generated';
import { getBenchmarkPickerV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdBenchmarksGet } from '../../generated';

/*
 * GET /v1/workspaces/{workspace_id}/assets/images/collections/{collection_id}/benchmarks: the benchmarks a collection
 * can be scored against, grouped as the picker lists them (the Vizit benchmarks, then the workspace's custom sets of
 * the collection's image type). The API decides the grouping, the order and which entries are disabled, e.g. a set
 * under its minimum. `toCollectionBenchmarkPicker` is the one place the generated `BenchmarkPickerView` is mapped.
 * Tests answer this path with `tests/mocks/collection-scoring-handlers.ts`.
 */

export type BenchmarkPickerKind = BenchmarkPickerEntry['kind'];

/** One benchmark the collection can be scored against, mapped for the UI. */
export interface CollectionBenchmarkPickerEntry {
  kind: BenchmarkPickerKind;
  /** Set for custom benchmarks; `null` for Vizit ones, which are addressed by `retailer`. */
  id: string | null;
  retailer: Retailer | null;
  name: string;
  description: string | null;
  /** `null` for Vizit benchmarks, whose pool depends on the product category. */
  imageCount: number | null;
  minimumImages: number | null;
  recommendedImages: number | null;
  /** True when the benchmark cannot score the collection yet, as the API decides it. */
  disabled: boolean;
}

export interface CollectionBenchmarkPickerGroup {
  kind: BenchmarkPickerKind;
  /** The group heading as the API words it. */
  label: string;
  benchmarks: CollectionBenchmarkPickerEntry[];
}

export interface CollectionBenchmarkPicker {
  groups: CollectionBenchmarkPickerGroup[];
}

export function toCollectionBenchmarkPickerEntry(entry: BenchmarkPickerEntry): CollectionBenchmarkPickerEntry {
  return {
    kind: entry.kind,
    id: entry.id,
    retailer: entry.retailer,
    name: entry.name,
    description: entry.description,
    imageCount: entry.image_count,
    minimumImages: entry.minimum_images,
    recommendedImages: entry.recommended_images,
    disabled: entry.disabled,
  };
}

export function toCollectionBenchmarkPicker(response: BenchmarkPickerView): CollectionBenchmarkPicker {
  return {
    groups: response.groups.map((group) => ({
      kind: group.kind,
      label: group.label,
      benchmarks: group.benchmarks.map(toCollectionBenchmarkPickerEntry),
    })),
  };
}

export interface GetCollectionBenchmarkPickerQueryVariables {
  workspace_id: string;
  collection_id: string;
}

export type GetCollectionBenchmarkPickerQuery = CollectionBenchmarkPicker;

async function getCollectionBenchmarkPicker({
  workspace_id,
  collection_id,
}: GetCollectionBenchmarkPickerQueryVariables) {
  const { data } = await getBenchmarkPickerV1WorkspacesWorkspaceIdAssetsImagesCollectionsCollectionIdBenchmarksGet({
    path: { workspace_id, collection_id },
    throwOnError: true,
  });
  return toCollectionBenchmarkPicker(data);
}

/** How many times a failed read is tried again before the picker shows the error. */
const MAX_PICKER_RETRIES = 3;

/**
 * Whether to read the picker again after a failure: a server or network error up to three more times, with the
 * default backoff, and a 4xx never, since it would answer the same. The app's default allows four retries on most
 * errors, which kept a failing route busy for 15 seconds before the picker could say so.
 */
export function shouldRetryCollectionBenchmarkPicker(failureCount: number, error: unknown) {
  // Unit tests never retry, the repo-wide idiom: suites share one QueryClient under fake timers.
  if (import.meta.env.VITEST) {
    return false;
  }
  if (error instanceof APIError && error.status >= 400 && error.status < 500) {
    return false;
  }
  return failureCount < MAX_PICKER_RETRIES;
}

export const useGetCollectionBenchmarkPickerQuery = <TData = GetCollectionBenchmarkPickerQuery, TError = unknown>(
  variables: GetCollectionBenchmarkPickerQueryVariables,
  options?: Omit<UseQueryOptions<GetCollectionBenchmarkPickerQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetCollectionBenchmarkPickerQuery, TError, TData>({
    queryKey: useGetCollectionBenchmarkPickerQuery.getKey(variables),
    queryFn: () => getCollectionBenchmarkPicker(variables),
    retry: shouldRetryCollectionBenchmarkPicker,
    ...options,
  });
};

useGetCollectionBenchmarkPickerQuery.getKey = ({
  workspace_id,
  collection_id,
}: GetCollectionBenchmarkPickerQueryVariables) => ['GetCollectionBenchmarkPicker', { workspace_id, collection_id }];

useGetCollectionBenchmarkPickerQuery.fetcher = (variables: GetCollectionBenchmarkPickerQueryVariables) => () =>
  getCollectionBenchmarkPicker(variables);
